if (!customElements.get('simple-slider')) {
  class SimpleSlider extends HTMLElement {
    constructor() {
      super();

      // Slider elements
      this.sliderList = this.querySelector('[data-list]');
      this.left = this.querySelector('[data-left]');
      this.right = this.querySelector('[data-right]');
      this.dots = this.querySelectorAll('[data-dot]');
      this.indicatorFill = this.querySelector('[data-indicator-fill]');
      this.thumbnails = this.querySelector('[data-thumbnails]');
      this.number = this.querySelector('[data-number]');

      // Initial state
      this.slide = this.sliderList?.children[0] || null;
      this.currentIndex = 0;
      this.isMouseDown = false;
      this.startX = 0;
      this.dragged = false; // Track whether dragging occurred

      this.setupEventListeners();

      this.setupObserver();

      this.visibilityNavigation();
      this.updateIndicator();
      this.visibilityThumbnails();
      this.updateNumber();
    }

    setupEventListeners() {
      this.left?.addEventListener('click', () => this.scrollLeft());
      this.right?.addEventListener('click', () => this.scrollRight());

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.scrollToSlide(index);
        });
      });

      if (this.thumbnails) {
        [...this.thumbnails.children].forEach((thumb, index) => {
          thumb.addEventListener('click', () => {
            this.scrollToSlide(index);
          });
        });
      }

      if (window.innerWidth >= 750) {
        // Mouse drag support
        this.sliderList.addEventListener('mousedown', this.onMouseDown.bind(this));
        this.sliderList.addEventListener('mousemove', this.onMouseMove.bind(this));
        this.sliderList.addEventListener('mouseup', this.onMouseUp.bind(this));
        this.sliderList.addEventListener('mouseleave', this.onMouseUp.bind(this));

        // Prevent link navigation on drag
        this.sliderList.querySelectorAll('a').forEach((link) => {
          link.addEventListener('click', (event) => {
            if (this.dragged) {
              event.preventDefault();
            }
          });
        });

        // Prevent play video on drag
        this.sliderList.querySelectorAll('custom-video').forEach((video) => {
          video.addEventListener('click', (event) => {
            if (this.dragged) {
              event.stopImmediatePropagation();
            }
          });
        });
      }
    }

    onMouseDown(event) {
      this.isMouseDown = true;
      this.startX = event.pageX - this.sliderList.offsetLeft;
      this.dragged = false; // Reset dragged state on new drag
      this.sliderList.style.cursor = 'grabbing'; // Change cursor to indicate dragging
    }

    onMouseMove(event) {
      if (!this.isMouseDown) return;
      const moveX = event.pageX - this.sliderList.offsetLeft;
      const distance = moveX - this.startX;

      // If movement is significant, set dragged to true
      if (Math.abs(distance) > 5) {
        this.dragged = true;
      }

      if (distance < 0) {
        this.scrollRight();
      } else {
        this.scrollLeft();
      }
    }

    onMouseUp() {
      this.isMouseDown = false;
      this.sliderList.style.cursor = 'grab'; // Reset cursor
    }

    scrollToSlide(index) {
      if (!this.sliderList || !this.slide) return;

      const maxIndex = this.sliderList.children.length - 1;
      const targetIndex = Math.max(0, Math.min(index, maxIndex));

      this.sliderList.scrollTo({
        left: this.slide.clientWidth * targetIndex,
        behavior: 'smooth',
      });
    }

    scrollLeft() {
      this.sliderList.scrollLeft -= this.slide.clientWidth;
    }

    scrollRight() {
      this.sliderList.scrollLeft += this.slide.clientWidth;
    }

    setupObserver() {
      const sliderScrollObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.currentIndex = parseInt(entry.target.dataset.index, 10);
              this.visibilityNavigation();
              this.visibilityDots();
              this.updateIndicator();
              this.updateNumber();
              this.visibilityThumbnails();
              this.scrollThumbnailIntoView();
            }
          });
        },
        {
          threshold: 0.6, // Adjusted to ensure proper detection of slide visibility
        }
      );

      // Assign `data-index` to each slide for identification
      [...this.sliderList.children].forEach((slide, index) => {
        slide.dataset.index = index;
        sliderScrollObserver.observe(slide);
      });
    }

    visibilityNavigation() {
      const size = this.sliderList.children.length - 1;
      // Check if at the first or last slide
      if (this.currentIndex === 0) {
        this.left?.setAttribute('data-active', 'false');
      } else {
        this.left?.setAttribute('data-active', 'true');
      }

      if (this.currentIndex === size) {
        this.right?.setAttribute('data-active', 'false');
      } else {
        this.right?.setAttribute('data-active', 'true');
      }
    }

    visibilityDots() {
      this.dots.forEach((dot) => dot?.removeAttribute('data-active'));
      this.dots[this.currentIndex]?.setAttribute('data-active', '');
    }

    visibilityThumbnails() {
      if (!this.thumbnails) return;

      const thumbnails = [...this.thumbnails.children];
      thumbnails.forEach((thumb) => thumb?.removeAttribute('data-active'));
      thumbnails[this.currentIndex]?.setAttribute('data-active', '');
    }

    scrollThumbnailIntoView() {
      if (!this.thumbnails) return;

      // Thumbnails are horizontal on mobile/tablet and vertical on desktop (>= 1200px),
      // matching the layout defined in service-section.css
      const isVerticalLayout = window.innerWidth >= 1200;

      if (isVerticalLayout) {
        this.scrollThumbnailVertically();
      } else {
        this.scrollThumbnailHorizontally();
      }
    }

    scrollThumbnailVertically() {
      const container = this.thumbnails;
      const thumbnails = [...container.children];
      const currentThumb = thumbnails[this.currentIndex];

      if (!currentThumb) return;

      const containerHeight = container.clientHeight;
      const thumbTop = currentThumb.offsetTop - container.offsetTop;
      const thumbCenterOffset = thumbTop + currentThumb.clientHeight / 2;

      const targetScrollTop = thumbCenterOffset - containerHeight / 2;
      const maxScrollTop = container.scrollHeight - containerHeight;
      const nextScrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop));

      container.scrollTo({
        top: nextScrollTop,
        behavior: 'smooth',
      });
    }

    scrollThumbnailHorizontally() {
      const container = this.thumbnails;
      const thumbnails = [...container.children];
      const currentThumb = thumbnails[this.currentIndex];

      if (!currentThumb) return;

      const containerWidth = container.clientWidth;
      const thumbLeft = currentThumb.offsetLeft - container.offsetLeft;
      const thumbCenterOffset = thumbLeft + currentThumb.clientWidth / 2;

      const targetScrollLeft = thumbCenterOffset - containerWidth / 2;
      const maxScrollLeft = container.scrollWidth - containerWidth;
      const nextScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));

      container.scrollTo({
        left: nextScrollLeft,
        behavior: 'smooth',
      });
    }

    updateIndicator() {
      if (!this.indicatorFill || !this.sliderList) return;

      const slidesCount = this.sliderList.children.length;

      if (slidesCount <= 1) {
        this.indicatorFill.style.setProperty('--slider-indicator-progress', '0%');
        return;
      }

      const maxIndex = slidesCount - 1;
      const progress = (this.currentIndex / maxIndex) * 100;

      this.indicatorFill.style.setProperty('--slider-indicator-progress', `${progress}%`);
    }

    updateNumber() {
      if (!this.number) return;
      this.number.textContent = String(this.currentIndex + 1);
    }
  }

  customElements.define('simple-slider', SimpleSlider);
}
